// ============================================================================
// MODULE NAVIGATION BRIDGE
// ============================================================================
// Tiny bridge that lets a module-tier screen open another module's tab and
// pass it a small typed payload, without forcing the layout's tab system to
// thread arbitrary params through every render.
//
// Single-instance pattern (default):
//   openModule('fsa-engagement', { engagementId: row.id });
//   ↳ stores params under 'app:module-params:fsa-engagement'
//   ↳ dispatches 'module:navigate' with { moduleId: 'fsa-engagement' }
//
// Multi-instance pattern (FSA-2.2 — per-engagement workspace tab):
//   openModule(
//     'fsa-engagement',
//     { engagementId: row.id },
//     { instanceKey: String(row.id), tabTitle: `${client} (${code}) · FSA` },
//   );
//   ↳ stores params under 'app:module-params:fsa-engagement:<engagementId>'
//   ↳ dispatches with { moduleId: 'fsa-engagement:<engagementId>',
//                       baseModuleId: 'fsa-engagement', tabTitle: '…' }
//   ↳ layout creates / refocuses the composite tab id; config lookup falls
//     back to the base id; tab title comes from the event detail.
//
// Why sessionStorage: survives reloads + tab persistence, isolated to the
// active browser session, no React context required so any screen (or
// even non-React code) can publish.
//
// Module boundary: this file lives outside `components/modules/`, so basic-
// tier files can import it freely without violating the basic ← FSA rule.

const STORAGE_PREFIX = 'app:module-params:';
const NAVIGATE_EVENT = 'module:navigate';

export interface ModuleNavigateDetail {
  /** Composite tab id (`<base>` for singleton, `<base>:<instanceKey>` for multi-instance). */
  moduleId: string;
  /** The base module id used for config lookup (always present). */
  baseModuleId: string;
  /** Optional per-instance tab title — when set, layout overrides the dynamic-module title. */
  tabTitle?: string;
}

export interface OpenModuleOptions {
  /**
   * Optional opaque key that turns a singleton module into a multi-instance
   * one. Two opens of the same `moduleId` with different `instanceKey`s
   * each get their own tab; with the same `instanceKey` the existing tab
   * is refocused.
   */
  instanceKey?: string;
  /** Tab strip label override. Required when `instanceKey` is set (otherwise every instance looks the same). */
  tabTitle?: string;
}

/**
 * Build the composite tab id from a base module id + optional instance key.
 * Used by both `openModule` and the layout to keep the convention in one place.
 */
export function composeTabId(baseModuleId: string, instanceKey?: string): string {
  if (!instanceKey) return baseModuleId;
  return `${baseModuleId}:${instanceKey}`;
}

/** Extract the base module id from a (possibly composite) tab id. */
export function baseModuleIdOf(tabId: string): string {
  const idx = tabId.indexOf(':');
  return idx === -1 ? tabId : tabId.slice(0, idx);
}

/** Persists params for a target module and triggers tab navigation. */
export function openModule(
  baseModuleId: string,
  params?: Record<string, unknown>,
  options?: OpenModuleOptions,
): void {
  if (typeof window === 'undefined') return;
  const tabId = composeTabId(baseModuleId, options?.instanceKey);
  try {
    if (params) {
      window.sessionStorage.setItem(STORAGE_PREFIX + tabId, JSON.stringify(params));
    } else {
      window.sessionStorage.removeItem(STORAGE_PREFIX + tabId);
    }
  } catch {
    // sessionStorage unavailable (private mode etc.) — params are dropped silently.
  }
  window.dispatchEvent(
    new CustomEvent<ModuleNavigateDetail>(NAVIGATE_EVENT, {
      detail: { moduleId: tabId, baseModuleId, tabTitle: options?.tabTitle },
    }),
  );
}

/**
 * Reads params previously stored for a (possibly composite) tab id, or null
 * when none. Callers typically pass their own `tabId` prop so each instance
 * resolves to its own payload.
 */
export function getModuleParams<T = Record<string, unknown>>(tabId: string): T | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + tabId);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/** Clears params for a tab id (call when the tab closes, optional). */
export function clearModuleParams(tabId: string): void {
  if (typeof window === 'undefined') return;
  try { window.sessionStorage.removeItem(STORAGE_PREFIX + tabId); } catch { /* noop */ }
}

export const MODULE_NAVIGATE_EVENT = NAVIGATE_EVENT;
